import {
  Layers,
  PaintRoller,
  Grid3X3,
  Waypoints,
  Droplets,
  PlugZap,
  Hammer,
} from "lucide-react";
import { InstagramIcon, FacebookIcon, TikTokIcon } from "../components/socialIcons";

// ============================================================
// KONFIGURASI UTAMA TOKO — cukup edit file ini untuk mengganti
// nama, alamat, WhatsApp, kategori, jam buka, maps, dan logo.
// Field kosong ("" atau []) otomatis disembunyikan dari website.
// ============================================================

export const site = {
  name: "Nuansa Jaya Abadi",
  shortName: "NJA",
  legalName: "CV. Nuansa Jaya Abadi",
  tagline: "Toko Bangunan di Bajawa",
  address: "Jl. Basuki Rahmat, Tanalodu, Bajawa",
  city: "Bajawa",
  region: "Kabupaten Ngada, Flores, Nusa Tenggara Timur",
  whatsappDisplay: "0851-4172-2822",
  whatsappNumber: "6285141722822",
  phoneIntl: "+62-851-4172-2822",
  googleMapsUrl: "https://maps.app.goo.gl/y5Tfyf88b5NZ5dN46",
  googleMapsEmbedUrl: "https://www.google.com/maps?q=-8.7902352,120.9748782+(Nuansa+Jaya+Abadi)&z=17&output=embed",
  openingHours: ["Senin–Sabtu: 08.00–17.30 WITA"],
  socials: [
    { label: "Instagram", url: "https://www.instagram.com/nuansajayaabadi/", icon: InstagramIcon },
    { label: "Facebook", url: "https://www.facebook.com/nuansa.jaya.abadi.2025", icon: FacebookIcon },
    { label: "TikTok", url: "https://www.tiktok.com/@nuansa_jaya_abadi", icon: TikTokIcon },
  ],
  logoUrl: "/logo.png", // logo asli NJA lengkap dengan teks (file di folder public)
  logoMarkUrl: "/logo-mark.png", // mark NJA tanpa teks — dipakai di header/footer/favicon
  storePhotoUrl: "/foto-toko-1080.webp",
  storePhotoSrcSet: "/foto-toko-640.webp 640w, /foto-toko-1080.webp 1080w",
  storePhotoAlt: "Tampak depan toko Nuansa Jaya Abadi di Bajawa",
  galleryPhotos: [
    { src: "/foto-toko-1080.webp", alt: "Tampak depan toko Nuansa Jaya Abadi di Bajawa" },
    // Tambahkan foto toko lain di sini, mis.: { src: "/galeri-rak.webp", alt: "Rak barang di toko NJA" },
  ],
  shoppingInfo: {
    // Isi HANYA yang sudah dikonfirmasi pemilik. Contoh isian:
    // satuanGrosir: "Melayani pembelian satuan dan grosir",
    // pengantaran: "Tersedia pengantaran untuk area Bajawa",
    // wilayah: "Bajawa dan sekitarnya",
    // ongkir: "Ongkir menyesuaikan jarak",
    // pembayaran: "Tunai dan transfer",
    // penawaranProyek: "Kirim daftar kebutuhan proyek via WhatsApp untuk penawaran",
  },
  brands: [], // contoh: ["Mowilex"] — isi HANYA merek yang dikonfirmasi dijual di toko
};

export const waLink = (message) =>
  `https://wa.me/${site.whatsappNumber}?text=${encodeURIComponent(message)}`;

export const waGeneralMessage =
  "Halo NJA, saya ingin menanyakan harga dan ketersediaan barang. Kebutuhan saya: ";

export const waCategoryMessage = (name) =>
  `Halo NJA, saya ingin menanyakan harga dan ketersediaan kategori ${name}. Kebutuhan saya: `;

export const waInfoMessage =
  "Halo NJA, saya ingin menanyakan informasi pengantaran, pembayaran, dan kebutuhan proyek. Kebutuhan saya: ";

// ============================================================
// PRODUK UNGGULAN — isi data NYATA di bawah, lalu set published: true.
// Format isian per produk (salin blok ini):
// {
//   name: "Semen Tiga Roda 40 kg",
//   category: "semen-dan-bahan-bangunan", // slug kategori
//   brand: "Tiga Roda",                    // opsional
//   photo: "/produk/semen-tiga-roda.webp", // opsional; tanpa foto tampil ikon kategori
//   spec: "Zak 40 kg",                     // opsional
//   price: "",                             // opsional; kosong → "Hubungi untuk harga"
//   published: true,
// },
// Produk dengan published: false / data kosong TIDAK tampil ke publik.
// ============================================================
export const products = [];

export const publishedProducts = products.filter((p) => p.published);

export const waProductMessage = (p) =>
  `Halo NJA, saya ingin menanyakan harga dan ketersediaan ${p.name}${p.spec ? `, ${p.spec}` : ""}.`;

// Daftar kategori awal (draft — menunggu konfirmasi pemilik toko).
export const categories = [
  {
    slug: "bahan-bangunan",
    name: "Bahan Bangunan",
    icon: Layers,
    shortDesc:
      "Semen, bata, dan bahan dasar untuk pekerjaan struktur, pasangan, dan plester.",
    intro:
      "Bahan dasar seperti semen dan material pasangan adalah awal dari hampir semua pekerjaan bangunan dan renovasi di Bajawa, mulai dari pondasi sampai plester dinding.",
    kegunaan: [
      "Pekerjaan pondasi, sloof, dan cor lantai",
      "Pasangan dinding bata atau batako",
      "Plester dan acian dinding",
      "Perbaikan struktur rumah saat renovasi",
    ],
    pertimbangan: [
      "Jenis pekerjaan menentukan jenis semen atau bahan yang cocok",
      "Hitung perkiraan volume atau luas pekerjaan terlebih dahulu",
      "Simpan semen di tempat kering agar tidak menggumpal",
    ],
    infoDitanyakan: [
      "Jenis pekerjaan (cor, pasangan, plester, dan sebagainya)",
      "Perkiraan volume atau luas pekerjaan",
      "Merek yang biasa Anda atau tukang Anda pakai",
    ],
  },
  {
    slug: "cat",
    name: "Cat",
    icon: PaintRoller,
    shortDesc:
      "Cat tembok, cat kayu dan besi, serta bahan pendukung pengecatan.",
    intro:
      "Pemilihan cat yang tepat membuat hasil renovasi lebih rapi dan tahan lama, baik untuk dinding interior maupun eksterior yang terkena cuaca.",
    kegunaan: [
      "Pengecatan dinding interior dan eksterior",
      "Finishing kayu, kusen, dan besi",
      "Pelapisan ulang saat renovasi rumah",
    ],
    pertimbangan: [
      "Pilih cat khusus interior atau eksterior sesuai lokasi",
      "Hitung luas permukaan untuk memperkirakan jumlah kaleng",
      "Siapkan kode warna atau contoh warna yang diinginkan",
    ],
    infoDitanyakan: [
      "Jenis permukaan (tembok, kayu, atau besi)",
      "Perkiraan luas area yang akan dicat",
      "Warna atau merek yang diinginkan",
    ],
  },
  {
    slug: "keramik",
    name: "Keramik",
    icon: Grid3X3,
    shortDesc:
      "Keramik lantai dan dinding dalam berbagai ukuran dan motif.",
    intro:
      "Keramik lantai dan dinding tersedia dalam banyak ukuran dan motif. Memilih tekstur yang tepat penting terutama untuk area yang sering basah.",
    kegunaan: [
      "Lantai ruangan, teras, dan garasi",
      "Dinding kamar mandi dan dapur",
      "Penggantian lantai saat renovasi",
    ],
    pertimbangan: [
      "Tentukan ukuran yang diinginkan (mis. 40x40 atau 60x60)",
      "Pilih permukaan kasar untuk area basah seperti kamar mandi",
      "Siapkan luas ruangan dan tambahkan cadangan sekitar 5–10%",
    ],
    infoDitanyakan: [
      "Ukuran dan motif keramik yang dicari",
      "Luas ruangan yang akan dipasang",
      "Perkiraan jumlah dus yang dibutuhkan",
    ],
  },
  {
    slug: "pipa-dan-sambungan",
    name: "Pipa & Sambungan",
    icon: Waypoints,
    shortDesc:
      "Pipa PVC, fitting, lem pipa, dan perlengkapan instalasi air.",
    intro:
      "Instalasi air bersih dan saluran pembuangan membutuhkan pipa serta sambungan dengan diameter yang sesuai agar tidak bocor dan mudah dirawat.",
    kegunaan: [
      "Instalasi air bersih di rumah",
      "Saluran pembuangan dan air hujan",
      "Perbaikan kebocoran pipa",
    ],
    pertimbangan: [
      "Pastikan diameter pipa (mis. ½, ¾, 1, 3, atau 4 inci)",
      "Sesuaikan jenis sambungan dengan arah instalasi",
      "Untuk perbaikan, foto instalasi lama sangat membantu",
    ],
    infoDitanyakan: [
      "Diameter dan perkiraan panjang pipa",
      "Daftar sambungan yang dibutuhkan",
      "Foto instalasi bila untuk perbaikan",
    ],
  },
  {
    slug: "sanitair-kran",
    name: "Sanitair & Kran",
    icon: Droplets,
    shortDesc:
      "Kran, shower, kloset, wastafel, dan perlengkapan kamar mandi.",
    intro:
      "Perlengkapan sanitair seperti kran, shower, dan kloset perlu disesuaikan dengan instalasi yang ada agar pemasangan berjalan lancar.",
    kegunaan: [
      "Perlengkapan kamar mandi dan dapur",
      "Penggantian kran atau shower yang rusak",
      "Melengkapi kamar mandi saat renovasi",
    ],
    pertimbangan: [
      "Ukuran drat kran yang umum dipakai adalah ½ inci",
      "Tentukan model kloset duduk atau jongkok",
      "Sesuaikan dengan instalasi air yang sudah ada",
    ],
    infoDitanyakan: [
      "Jenis barang dan model yang dicari",
      "Ukuran atau spesifikasi yang dibutuhkan",
      "Foto barang lama bila untuk penggantian",
    ],
  },
  {
    slug: "perlengkapan-listrik",
    name: "Perlengkapan Listrik",
    icon: PlugZap,
    shortDesc:
      "Kabel, saklar, stop kontak, lampu, fitting, dan MCB.",
    intro:
      "Perlengkapan listrik seperti kabel, saklar, dan lampu perlu dipilih sesuai beban dan daya rumah agar aman digunakan.",
    kegunaan: [
      "Penambahan titik lampu atau stop kontak",
      "Penggantian saklar dan fitting yang rusak",
      "Penerangan rumah dan teras",
    ],
    pertimbangan: [
      "Ukuran kabel harus sesuai beban listrik",
      "Ampere MCB disesuaikan dengan daya rumah",
      "Gunakan jasa tukang listrik untuk pemasangan",
    ],
    infoDitanyakan: [
      "Jenis dan ukuran barang (mis. kabel 2,5 mm)",
      "Jumlah titik atau unit yang dibutuhkan",
      "Merek yang biasa Anda pakai",
    ],
  },
  {
    slug: "perkakas",
    name: "Perkakas",
    icon: Hammer,
    shortDesc:
      "Palu, gergaji, meteran, obeng, dan perkakas kerja lainnya.",
    intro:
      "Perkakas yang tepat membuat pekerjaan pertukangan dan perbaikan rumah lebih cepat dan rapi, baik untuk tukang maupun pemilik rumah.",
    kegunaan: [
      "Pekerjaan pertukangan kayu dan pasangan",
      "Perbaikan dan pemasangan di rumah",
      "Pelengkap peralatan kerja tukang",
    ],
    pertimbangan: [
      "Pilih perkakas sesuai jenis pekerjaan",
      "Perhatikan ukuran yang umum dipakai",
      "Tanyakan ketersediaan bila butuh jenis tertentu",
    ],
    infoDitanyakan: [
      "Jenis perkakas yang dicari",
      "Ukuran atau spesifikasi",
      "Jumlah yang dibutuhkan",
    ],
  },
];

export const getCategory = (slug) =>
  categories.find((c) => c.slug === slug);

export const faq = [
  {
    q: "Di mana lokasi Toko Bangunan Nuansa Jaya Abadi?",
    a: "Toko kami berada di Jl. Basuki Rahmat, Tanalodu, Bajawa, Kabupaten Ngada, Nusa Tenggara Timur. Silakan hubungi kami via WhatsApp bila membutuhkan arah.",
  },
  {
    q: "Bagaimana cara menanyakan harga atau ketersediaan barang?",
    a: "Kirim pesan WhatsApp ke 0851-4172-2822 dengan menyebutkan jenis barang, ukuran, dan jumlah yang dibutuhkan. Kami akan membalas dengan informasi harga dan ketersediaan.",
  },
  {
    q: "Apa saja jenis barang yang tersedia di NJA?",
    a: "Kami menyediakan bahan bangunan, cat, keramik, pipa dan sambungan, sanitair dan kran, perlengkapan listrik, serta perkakas.",
  },
  {
    q: "Apakah bisa meminta penawaran untuk kebutuhan renovasi?",
    a: "Bisa. Kirimkan daftar kebutuhan renovasi Anda melalui WhatsApp, dan kami akan membantu menyiapkan informasi harga untuk setiap item.",
  },
];
